import Button from "@components/ui/Button";
import Spinner from "@components/ui/Spinner";
import ErrorBanner from "@components/shared/ErrorBanner";
import { useReactivationSettings } from "@hooks/useReactivationSettings";
import AttemptGapsEditor from "@components/scheduling/settings/AttemptGapsEditor";
import CallingSlotsEditor from "@components/scheduling/settings/CallingSlotsEditor";
import CallingDaysEditor from "@components/scheduling/settings/CallingDaysEditor";
import ProceduresEditor from "@components/scheduling/settings/ProceduresEditor";
import SmsTemplateEditor from "@components/scheduling/settings/SmsTemplateEditor";
import DailyReportEditor from "@components/scheduling/settings/DailyReportEditor";
import TransferHoldEditor from "@components/scheduling/settings/TransferHoldEditor";
import DoNotCallList from "@components/scheduling/settings/DoNotCallList";
import {
  cooldownProblem, settingsProblems,
} from "@components/scheduling/settings/settingsValidation";
import { canUseTransferCampaigns, isAdmin } from "@utils/access";

function Section({ title, hint, children }) {
  return (
    // Named, so a failure inside a section is findable as belonging to it.
    <section aria-label={title}
             className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {hint && <p className="text-xs text-gray-500">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

const inputClass =
  "w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

/**
 * What a campaign depends on: the hours it may call, how long it leaves a
 * patient alone, the confirmation text, the procedures on offer, and who must
 * never be called.
 *
 * The first three are held as a draft and written on an explicit Save, because
 * they are one settings document; procedures and the do-not-call list are their
 * own rows on the server and save as they are changed.
 *
 * The three come from three independent requests, so each section stands or
 * falls on its own: a clinic whose settings document cannot be read still gets
 * its procedures, and every failure is reported where it happened.
 */
export default function ReactivationSettingsTab({ clinicId, transferAllowed = canUseTransferCampaigns() }) {
  const hook = useReactivationSettings(clinicId);
  // The same rules the editors show in red, asked again here: red text the
  // clinic can save past only buys it a second message from the server.
  const blocked = settingsProblems(hook.draft).length > 0;
  const cooldownMessage = hook.draft ? cooldownProblem(hook.draft.cooldownDays) : null;

  if (hook.loading) {
    return <div className="flex justify-center py-10"><Spinner /></div>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        These settings apply to campaigns you start from now on. A campaign already running
        keeps the settings it started with.
      </p>

      {/* Without a settings document there is nothing to edit, but the reason
          has to be on screen with a way out. The endpoint 400s for a clinic
          with no scheduling provider and 404s for an admin who has not picked
          a clinic yet, and both used to leave the tab spinning for ever. */}
      {!hook.draft && (
        <section aria-label="Call settings"
                 className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <ErrorBanner message={hook.settingsError || "Call settings could not be loaded."} />
          <p className="text-xs text-gray-500">
            {isAdmin()
              ? "Call settings belong to one clinic. Pick a clinic in the tab bar, or check that this one is connected to its scheduling system."
              : "No reactivation settings yet. Check that the clinic is connected to its scheduling system."}
          </p>
          <Button color="light" size="sm" onClick={hook.reload}>Try again</Button>
        </section>
      )}

      {hook.draft && (
        <>
          <Section title="How often to try"
                   hint="A patient who doesn't answer, is busy or reaches voicemail is called again after each gap. Booked, declined and callback requests end the attempts.">
            <AttemptGapsEditor
              gaps={hook.draft.attemptGapsDays}
              onChange={(attemptGapsDays) => hook.setDraft({ attemptGapsDays })}
            />
          </Section>

          <Section title="When calls go out"
                   hint="Calls are placed inside these slots, in your clinic's time. Each follow-up tries a different slot.">
            <CallingSlotsEditor
              slots={hook.draft.callingSlots}
              onChange={(callingSlots) => hook.setDraft({ callingSlots })}
            />
          </Section>

          <Section title="Which days">
            <CallingDaysEditor
              days={hook.draft.callingDays}
              onChange={(callingDays) => hook.setDraft({ callingDays })}
            />
          </Section>

          <Section title="How long to leave a patient alone"
                   hint="After a call, a patient is hidden from the call list for this many days.">
            <div className="flex items-center gap-2">
              <input
                id="cooldown-days"
                type="number"
                min="0"
                step="1"
                className={inputClass}
                aria-label="Days to leave a patient alone"
                value={hook.draft.cooldownDays}
                // An empty box is left empty rather than read as 0: Number("")
                // is 0, and "no cooldown" is the one setting here that can
                // turn a campaign into harassment.
                onChange={(e) => hook.setDraft({
                  cooldownDays: e.target.value === "" ? "" : Number(e.target.value),
                })}
              />
              <span className="text-sm text-gray-600">
                {hook.draft.cooldownDays === 0 ? "days: no cooldown" : "days"}
              </span>
            </div>
            {cooldownMessage && (
              <p className="text-xs text-red-600">{cooldownMessage}</p>
            )}
          </Section>

          <Section title="Booking confirmation text">
            <SmsTemplateEditor
              enabled={hook.draft.smsEnabled}
              template={hook.draft.smsTemplate}
              placeholders={hook.draft.placeholders}
              segmentChars={hook.draft.segmentChars}
              onToggle={(smsEnabled) => hook.setDraft({ smsEnabled })}
              onTemplateChange={(smsTemplate) => hook.setDraft({ smsTemplate })}
            />
          </Section>

          <Section title="Daily report"
                   hint="An email at the end of each calling day: how many patients were called, reached, transferred or booked, and the result of every call.">
            <DailyReportEditor
              enabled={hook.draft.dailyReportEnabled}
              recipients={hook.draft.dailyReportRecipients}
              time={hook.draft.dailyReportTime}
              onToggle={(dailyReportEnabled) => hook.setDraft({ dailyReportEnabled })}
              onRecipientsChange={(dailyReportRecipients) => hook.setDraft({ dailyReportRecipients })}
              onTimeChange={(dailyReportTime) => hook.setDraft({ dailyReportTime })}
            />
          </Section>

          {/* The pause only means anything to a clinic whose campaigns hand
              patients to its front desk; the others never see the card. */}
          {transferAllowed && (
            <Section title="Pause after a transfer"
                     hint="Nothing is dropped while the pause runs: the queue waits, and no attempt is counted.">
              <TransferHoldEditor
                minutes={hook.draft.transferHoldMinutes}
                onChange={(transferHoldMinutes) => hook.setDraft({ transferHoldMinutes })}
              />
            </Section>
          )}

          {(hook.dirty || hook.settingsError) && (
            <div className="sticky bottom-0 space-y-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
              {/* A rejected save reports beside the button that asked for it. */}
              <ErrorBanner message={hook.settingsError} onDismiss={hook.clearSettingsError} />
              {hook.dirty && (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  {/* Named for the three sections above it: this bar stays
                      pinned while the clinic edits a procedure, which saves
                      itself and has nothing to do with Save. */}
                  <span className="mr-auto text-sm text-gray-700">
                    {blocked
                      ? "Fix the problems above before saving."
                      : "You have unsaved call settings."}
                  </span>
                  <Button color="light" size="sm" onClick={hook.discard} disabled={hook.saving}>
                    Discard
                  </Button>
                  <Button color="primary" size="sm" onClick={hook.save}
                          disabled={hook.saving || blocked}>
                    {hook.saving ? "Saving…" : "Save call settings"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      <Section title="What a campaign can offer"
               hint="Each procedure books for its own length. Changes here take effect immediately.">
        <ProceduresEditor
          treatments={hook.treatments}
          error={hook.treatmentsError}
          onDismissError={hook.clearTreatmentsError}
          onAdd={hook.addTreatment}
          onEdit={hook.editTreatment}
        />
      </Section>

      <Section title="Patients to never call">
        <DoNotCallList
          patients={hook.doNotCall}
          error={hook.doNotCallError}
          onDismissError={hook.clearDoNotCallError}
          onAllowCalls={hook.allowCalls}
        />
      </Section>
    </div>
  );
}
